import React, { useState } from 'react';
import { Menu } from 'lucide-react';
import { useSettings } from '../../hooks/useSettings';

// Custom hooks
import { useAdminNavigation } from '../../hooks/admin/useAdminNavigation';
import { useAdminData } from '../../hooks/admin/useAdminData';
import { useSubscriptionPlans } from '../../hooks/admin/useSubscriptionPlans';
import { useAdminSettings } from '../../hooks/admin/useAdminSettings';
import { useUserManagement } from '../../hooks/admin/useUserManagement';

// Components
import AdminSidebar from '../admin/AdminSidebar';
import TabRenderer from './TabRenderer';

/**
 * Refactored AdminDashboard - Main admin interface
 * Responsibilities: Orchestration and UI layout only
 * State management extracted to custom hooks
 */
const AdminDashboard = () => {
  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Navigation hook
  const { activeTab, handleTabChange, handleLogout } = useAdminNavigation();
  
  // Data management hooks
  const adminData = useAdminData(activeTab);
  const subscriptionPlans = useSubscriptionPlans(activeTab);
  const adminSettings = useAdminSettings(activeTab);
  const userManagement = useUserManagement();
  
  // App settings
  const { 
    settings: appSettings, 
    loading: appSettingsLoading, 
    settingsLoaded: appSettingsLoaded, 
    safeSettings: appSafeSettings 
  } = useSettings();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <div className="mobile-header md:hidden">
        <button
          onClick={() => setSidebarOpen(true)}
          className="bg-white/10 hover:bg-white/20 rounded p-2 transition-colors"
        >
          <Menu size={20} />
        </button>
        <div className="flex items-center gap-2 ml-3">
          {appSettings?.product_logo && (
            <img src={appSettings.product_logo} alt="Logo" className="h-6 object-contain" />
          )}
          <span className="text-lg font-bold">{appSafeSettings?.product_name || 'Admin'}</span>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="mobile-overlay md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <AdminSidebar
        activeTab={activeTab}
        setActiveTab={handleTabChange}
        handleLogout={handleLogout}
        appSettings={appSettings}
        appSettingsLoaded={appSettingsLoaded}
        appSafeSettings={appSafeSettings}
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
      />

      {/* Main Content */}
      <div className="admin-content ml-80 min-h-screen mobile-content">
        <TabRenderer
          activeTab={activeTab}
          adminData={adminData}
          subscriptionPlans={subscriptionPlans}
          adminSettings={adminSettings}
          userManagement={userManagement}
          appSettings={appSettings}
        />
      </div>
    </div>
  );
};

export default AdminDashboard;